// Read-only catalog browser. Apple-esque two-pane layout: list on the
// left, detail on the right. Below the md breakpoint the list and the
// detail swap in-place with a sticky translucent back bar.
//
// Visual language is iOS 17 Settings / Health: large-title navigation
// bar on a tinted grouped-background, inset-grouped cards, hairline
// separators between rows, SF-Symbol-style line icons.
//
// Server enforces requireSession; the client guard here is cosmetic.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  getCatalogByBarcode,
  getCatalogItem,
  type ItemDetail,
  readSessionToken,
  type SearchFilters,
  searchCatalog,
} from "../data/catalog.ts";
import { ItemDetailView } from "./ItemDetail.tsx";
import { ItemList, type ListState } from "./ItemList.tsx";
import { ScanIcon } from "./icons.tsx";
import { SearchBar } from "./SearchBar.tsx";

type DetailState =
  | { kind: "idle" }
  | { kind: "loading"; id: string }
  | { kind: "loaded"; detail: ItemDetail }
  | { kind: "error"; status: number; error: string };

export function Catalog() {
  const token = readSessionToken();
  const [filters, setFilters] = useState<SearchFilters>({});
  const [state, setState] = useState<ListState>({ kind: "idle" });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailState>({ kind: "idle" });

  // Fetch the list on filter changes.
  useEffect(() => {
    if (!token) {
      setState({ kind: "unauthorized" });
      return;
    }
    let cancelled = false;
    setState({ kind: "loading" });
    searchCatalog(token, filters).then((r) => {
      if (cancelled) return;
      if (r.ok) {
        setState({ kind: "loaded", items: r.items, hasMore: r.hasMore });
      } else if (r.status === 401 || r.status === 403) {
        setState({ kind: "unauthorized" });
      } else {
        setState({ kind: "error", status: r.status, error: r.error });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [token, filters]);

  // Fetch the detail when selection changes.
  useEffect(() => {
    if (!selectedId || !token) {
      setDetail({ kind: "idle" });
      return;
    }
    let cancelled = false;
    setDetail({ kind: "loading", id: selectedId });
    getCatalogItem(token, selectedId).then((r) => {
      if (cancelled) return;
      if (r.ok) setDetail({ kind: "loaded", detail: r.detail });
      else setDetail({ kind: "error", status: r.status, error: r.error });
    });
    return () => {
      cancelled = true;
    };
  }, [selectedId, token]);

  const loadedDetail = useMemo(() => (detail.kind === "loaded" ? detail.detail : null), [detail]);
  const showDetail = selectedId != null;
  const itemCount = state.kind === "loaded" ? state.items.length : null;

  return (
    <main className="min-h-screen bg-[#f2f2f7] font-sans text-slate-900">
      <div className="md:flex md:min-h-screen md:items-stretch">
        {/* Master pane */}
        <section
          className={`${
            showDetail ? "hidden md:block" : "block"
          } md:flex-1 md:border-r md:border-slate-200/80 md:bg-[#f2f2f7]`}
        >
          <LargeTitleBar title="Products" subtitle={subtitleForCount(itemCount)} />
          <SearchBar filters={filters} onChange={setFilters} />
          <ItemList
            state={state}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onLoadMore={() => {
              if (state.kind !== "loaded" || !state.hasMore) return;
              setFilters((f) => ({
                ...f,
                offset: (f.offset ?? 0) + (f.limit ?? 25),
              }));
            }}
          />
        </section>

        {/* Detail pane */}
        <section
          className={`${showDetail ? "block" : "hidden md:block"} md:w-[520px] lg:w-[640px] bg-[#f2f2f7]`}
        >
          {detail.kind === "loading" && <DetailMessage text="Loading" />}
          {detail.kind === "error" && (
            <DetailMessage text={`${detail.status} · ${detail.error}`} tone="error" />
          )}
          {loadedDetail && (
            <ItemDetailView detail={loadedDetail} onBack={() => setSelectedId(null)} />
          )}
          {!showDetail && (
            <div className="hidden md:flex h-full items-center justify-center p-8">
              <EmptyStateScanner token={token} onItemFound={setSelectedId} />
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

function LargeTitleBar({ title, subtitle }: { title: string; subtitle?: string | null }) {
  return (
    <div className="px-5 md:px-8 pt-12 pb-5">
      <h1 className="text-[34px] md:text-[36px] leading-[1.05] font-bold tracking-tight text-slate-900">
        {title}
      </h1>
      {subtitle && <p className="mt-1 text-[17px] text-slate-500">{subtitle}</p>}
    </div>
  );
}

function DetailMessage({ text, tone }: { text: string; tone?: "error" }) {
  const cls = tone === "error" ? "text-[#ff3b30]" : "text-slate-400";
  return (
    <div className="h-full flex items-center justify-center p-12">
      <p className={`text-[17px] ${cls}`}>{text}</p>
    </div>
  );
}

type ScanState =
  | { kind: "idle" }
  | { kind: "looking"; code: string }
  | { kind: "notfound"; code: string }
  | { kind: "error"; message: string };

function EmptyStateScanner({
  token,
  onItemFound,
}: {
  token: string | null;
  onItemFound: (id: string) => void;
}) {
  const [input, setInput] = useState("");
  const [state, setState] = useState<ScanState>({ kind: "idle" });
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep the input focused so a hardware scanner (keyboard-wedge) types
  // straight in without the staffer tapping first. Re-focus after each
  // state transition so a notfound/error result doesn't leave focus
  // orphaned on the (now dismissed) message.
  // biome-ignore lint/correctness/useExhaustiveDependencies: state drives the re-focus on purpose; the effect body doesn't read it.
  useEffect(() => {
    inputRef.current?.focus();
  }, [state]);

  async function lookup(code: string) {
    const trimmed = code.trim();
    if (!trimmed) return;
    if (!token) {
      setState({ kind: "error", message: "Session has ended. Sign in again." });
      return;
    }
    setState({ kind: "looking", code: trimmed });
    const r = await getCatalogByBarcode(token, trimmed);
    if (r.ok) {
      onItemFound(r.item.id);
      setInput("");
      setState({ kind: "idle" });
      return;
    }
    if (r.status === 404) {
      setState({ kind: "notfound", code: trimmed });
      setInput("");
      return;
    }
    setState({ kind: "error", message: `${r.status} · ${r.error}` });
  }

  const message = (() => {
    if (state.kind === "looking") return `Looking up ${state.code}…`;
    if (state.kind === "notfound") return `No item with barcode ${state.code}.`;
    if (state.kind === "error") return state.message;
    return "Scan a tag or type a UPC / SKU to look up.";
  })();
  const messageTone =
    state.kind === "notfound" || state.kind === "error" ? "text-[#ff3b30]" : "text-slate-500";

  return (
    <div className="w-full max-w-md text-center">
      <div className="mx-auto h-24 w-24 rounded-3xl bg-ios-blue/10 text-ios-blue grid place-items-center">
        <ScanIcon className="w-14 h-14" />
      </div>
      <h2 className="mt-6 text-[22px] font-semibold text-slate-900 tracking-tight">
        Look up an item
      </h2>
      <p className={`mt-2 text-[17px] ${messageTone}`}>{message}</p>
      <form
        className="mt-5"
        onSubmit={(e) => {
          e.preventDefault();
          lookup(input);
        }}
      >
        <label className="flex items-center h-12 px-4 rounded-full bg-white border border-slate-200 shadow-[0_0.5px_0_rgba(0,0,0,0.04)] focus-within:border-ios-blue">
          <input
            ref={inputRef}
            type="text"
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            placeholder="Scan or type a barcode"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="flex-1 bg-transparent text-[17px] text-slate-900 placeholder:text-slate-400 focus:outline-none"
            aria-label="Barcode input"
          />
          {input.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setInput("");
                setState({ kind: "idle" });
                inputRef.current?.focus();
              }}
              className="ml-2 text-[15px] text-slate-500 font-medium"
            >
              Clear
            </button>
          )}
        </label>
      </form>
      <p className="mt-4 text-[15px] text-slate-400">Or pick an item from the list on the left.</p>
    </div>
  );
}

function subtitleForCount(n: number | null): string | null {
  if (n == null) return null;
  if (n === 0) return "No items";
  if (n === 1) return "1 item";
  return `${n} items`;
}
